<template>
  <div
    class="InsuranceOptionsForm"
    :id="props.id"
  >
    <UserCard :title="title" />

    <template
      v-for="(option, index) in insuranceOptions"
      :key="option.label"
    >
      <CheckHiringComponent
        :id="`${props.id}-insurance-option-${index}`"
        :label="option.label"
        :model-value="option.checked"
        @update:modelValue="(newValue) => updateChecked(index, newValue)"
      />

      <div v-if="option.checked && !option.hidden">
        <p
          v-if="option.code === 'GS09' || option.code === 'GS99'"
          class="label"
        >
          {{ t('hiring.common.insurance-options.insured-capital') }}
        </p>
        <AdvantageSliderComponent
          :id="`${props.id}-options-slider-${index}`"
          @update:modelValue="(newValue) => updateValue(index, newValue)"
          :model-value="option.value ? option.value : option.min"
          :min="option.min"
          :max="option.max"
          :step="option.step"
          :units="option.units"
          :period="option.period"
          :footer-message="`${option.footerMessage}${option.units}`"
        />
      </div>
    </template>

    <div
      v-if="!insuranceOptions.length"
      class="empty-message"
    >
      {{ t('copy_coverages_not_available') }}
    </div>

    <div
      ref="hiddenFieldWrapper"
      style="display: none"
      aria-hidden="true"
    >
      <v-text-field
        :key="String(innerValue)"
        ref="$field"
        aria-hidden="true"
        :aria-describedby="undefined"
        tabindex="-1"
        v-model="innerValue"
        :rules="validations"
        :hide-details="true"
      />
    </div>

    <ErrorMessageComponent :messages="fieldValidationErrorMessage" />
  </div>
</template>

<script lang="ts" setup>
  import ErrorMessageComponent from '@/modules/core/components/formFields/ErrorMessageComponent.vue'
  import AdvantageSliderComponent from '@/modules/core/components/utils/AdvantageSliderComponent.vue'
  import {
    endDuplicatedLabelControl,
    startDuplicatedLabelControl
  } from '@/modules/core/composables/accesibilityComposable'
  import { useValidationError } from '@/modules/core/composables/validationErrorsComposable'
  import type { CoverageOptionsInterface, ValidationRuleType } from '@/modules/core/models/types'
  import CheckHiringComponent from '@/modules/health/hiring/components/CheckHiringComponent.vue'
  import UserCard from '@/modules/health/hiring/components/UserCardForm.vue'
  import { computed, onMounted, onUnmounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  const { t } = useI18n()

  interface PropsInterface {
    id: string
    title: string
    insuranceOptions: CoverageOptionsInterface[]
    rules?: ValidationRuleType[]
  }

  const props = withDefaults(defineProps<PropsInterface>(), {
    insuranceOptions: () => [],
    rules: () => []
  })

  const emit = defineEmits(['updateOptions'])

  const validations: ValidationRuleType[] = props.rules

  const innerValue = computed(() => {
    return props.insuranceOptions.some((o) => o.checked)
  })

  function updateChecked(optionIndex: number, newValue: any) {
    const newOptions = [...props.insuranceOptions]
    newOptions[optionIndex].checked = newValue

    emit('updateOptions', newOptions)
  }

  function updateValue(optionIndex: number, newValue: any) {
    const newOptions = [...props.insuranceOptions]
    newOptions[optionIndex].value = newValue

    emit('updateOptions', newOptions)
  }

  const { $field, fieldValidationErrorMessage } = useValidationError()

  const hiddenFieldWrapper = ref<HTMLElement | null>(null)
  let hiddenObserver: MutationObserver | null = null

  onMounted(() => {
    hiddenObserver = startDuplicatedLabelControl(hiddenFieldWrapper.value, {
      labelOrphanedInputs: true
    })
  })
  onUnmounted(() => endDuplicatedLabelControl(hiddenObserver))
</script>

<style lang="scss" scoped>
  .InsuranceOptionsForm {
    .CheckHiringComponent {
      margin: $spacing-l 0;
    }

    .label {
      color: $color-neutral-dark;
      margin-bottom: $spacing-m;
    }

    .empty-message {
      padding: $spacing-m $spacing-s;
      color: $color-neutral;
    }
  }
</style>
